import { z } from 'zod';

export const InsightCard = z.object({
  title: z.string().trim().max(160).default(''),
  body: z.string().trim().max(1200).default(''),
});

export const InsightsSchemaV2 = z.object({
  schema_version: z.literal('insight.v2'),
  figure_caption: z.string().trim().max(600).default(''),
  insights: z.array(InsightCard).default([]),
  next_steps: z.array(z.string().trim().max(240)).default([]),
}).refine((val) => {
  const hasCaption = String(val.figure_caption || '').trim().length > 0;
  const hasSteps = Array.isArray(val.next_steps) && val.next_steps.length > 0;
  const hasCards = Array.isArray(val.insights) && val.insights.length > 0;
  return hasCaption || hasSteps || hasCards;
}, { message: 'At least one of caption, steps, or insights must be present' });

export type InsightItem = z.infer<typeof InsightsSchemaV2>['insights'][number];
export type InsightsPayload = z.infer<typeof InsightsSchemaV2>;

export function normalizeInsightsPayload(rawIn: any): InsightsPayload {
  const raw = rawIn ? { ...rawIn } : {};

  // Handle InsightV2Persistent format (with blocks array)
  if (Array.isArray(raw.blocks) && raw.blocks.length > 0) {
    const blocks = raw.blocks;
    // Extract figure_caption from block with kind === 'summary'
    const captionBlock = blocks.find((b: any) => b.kind === 'summary');
    const figure_caption = captionBlock?.text ? String(captionBlock.text).trim() : '';
    
    // Extract insights from blocks with kind === 'note'
    const noteBlocks = blocks.filter((b: any) => b.kind === 'note');
    const insights = noteBlocks.map((b: any) => {
      const text = String(b.text || '').trim();
      // Try to split title:body format
      const colonIdx = text.indexOf(':');
      if (colonIdx > 0 && colonIdx < text.length - 1) {
        return {
          title: text.substring(0, colonIdx).trim(),
          body: text.substring(colonIdx + 1).trim(),
        };
      }
      return { title: '', body: text };
    });
    
    // Extract next_steps from blocks with kind === 'suggestion'
    const suggestionBlocks = blocks.filter((b: any) => b.kind === 'suggestion');
    const next_steps = suggestionBlocks.map((b: any) => String(b.text || '').trim()).filter(Boolean);
    
    return {
      schema_version: 'insight.v2',
      figure_caption,
      insights,
      next_steps,
    };
  }

  // Merge likely carriers: top-level + nested insight_v2/insight + legacy takehome
  const merged: any = {
    ...raw,
    ...(raw.insight_v2 || {}),
    ...(raw.insight || {}),
    ...(raw.takehome || {}),
    ...(raw.data?.takehome || {}),
  };

  // Helpers
  const isEmptyArr = (x: any) => !Array.isArray(x) || x.length === 0;
  const coerceCards = (arr: any[]) =>
    (arr || [])
      .map((it: any, i: number) => {
        if (typeof it === 'string') return { title: `Result ${i + 1}`, body: it };
        const title =
          (typeof it?.title === 'string' && it.title) ||
          (typeof it?.heading === 'string' && it.heading) ||
          `Result ${i + 1}`;
        const body =
          (typeof it?.body === 'string' && it.body) ||
          (typeof it?.body_md === 'string' && it.body_md) ||
          (typeof it?.markdown === 'string' && it.markdown) ||
          (typeof it?.md === 'string' && it.md) ||
          (typeof it?.text === 'string' && it.text) ||
          '';
        return { title: String(title), body: String(body) };
      })
      .filter(c => (String(c.title || '').trim() || String(c.body || '').trim()));

  // CamelCase shims
  if (!merged.figure_caption && typeof merged.figureCaption === 'string') merged.figure_caption = merged.figureCaption;
  if (isEmptyArr(merged.next_steps) && Array.isArray(merged.nextSteps)) merged.next_steps = merged.nextSteps;
  if (isEmptyArr(merged.insights) && Array.isArray(merged.insightsV2)) merged.insights = merged.insightsV2;

  // Legacy shims (treat empty arrays as missing)
  if (isEmptyArr(merged.insights) && Array.isArray(merged.insights_v2)) merged.insights = merged.insights_v2;
  if (isEmptyArr(merged.insights) && Array.isArray(merged.results)) merged.insights = merged.results;
  if (isEmptyArr(merged.insights) && Array.isArray(merged.bullets)) merged.insights = merged.bullets;
  if (isEmptyArr(merged.next_steps) && typeof merged.next_step === 'string') merged.next_steps = [String(merged.next_step)];
  if (isEmptyArr(merged.next_steps) && typeof merged.next_intent === 'string') merged.next_steps = [String(merged.next_intent)];
  if (isEmptyArr(merged.next_steps) && typeof merged.nextIntent === 'string') merged.next_steps = [String(merged.nextIntent)];
  if (!merged.figure_caption && typeof merged.caption === 'string') merged.figure_caption = merged.caption;

  // Root-level markdown → synthesize a single card
  if (isEmptyArr(merged.insights)) {
    const rootMd =
      (typeof merged.markdown === 'string' && merged.markdown) ||
      (typeof merged.md === 'string' && merged.md) ||
      (typeof merged.text === 'string' && merged.text) ||
      '';
    if (rootMd.trim()) merged.insights = [{ title: '', body: rootMd }];
  }

  // IMPORTANT: coerce BEFORE parse so Zod sees proper cards
  if (Array.isArray(merged.insights)) merged.insights = coerceCards(merged.insights);

  merged.schema_version = 'insight.v2';

  const r = InsightsSchemaV2.safeParse(merged);
  if (r.success) return r.data;

  // Fallback with coercion
  const nextSteps = Array.isArray(merged.next_steps)
    ? merged.next_steps.map((s: any) => String(s)).filter(Boolean)
    : (typeof merged.next_intent === 'string' && merged.next_intent.trim()
        ? [String(merged.next_intent)]
        : (typeof merged.nextIntent === 'string' && merged.nextIntent.trim() ? [String(merged.nextIntent)] : []));

  const coerced =
    Array.isArray(merged.insights) ? coerceCards(merged.insights)
    : Array.isArray(merged.insights_v2) ? coerceCards(merged.insights_v2)
    : Array.isArray(merged.insightsV2) ? coerceCards(merged.insightsV2)
    : [];

  return {
    schema_version: 'insight.v2',
    figure_caption: String(merged.figure_caption || ''),
    insights: coerced,
    next_steps: nextSteps,
  };
}

export function isThirtyWords(s: string): boolean {
  const words = String(s || '').trim().split(/\s+/).filter(Boolean);
  return words.length === 30;
}


